import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { useContentful } from "frontend/hooks/use-contentful";
import { HeroTeaseEntry } from "frontend/contentful/schema/blocks";
import {
  HeroTease,
  HeroTeaseProps,
} from "design-system/components/blocks/hero-tease/hero-tease";
import { createHeroTeaseProps } from "./hero-tease.props";
import { createMediaAssetProps } from "../../primitives/media-asset.props";
import { RenderContentfulRichText } from "../../utils/render-contentful-rich-text";
import { createComponentHeaderProps } from "../../primitives/component-header.props";

interface HeroTeaseOptions {
  headerAbove?: boolean;
}

const entryLayoutToTeaseLayout: Record<
  NonNullable<HeroTeaseEntry["fields"]["layout"]>,
  HeroTeaseProps["layout"]
> = {
  card: "card",
  "text-only": "text-only",
  "flat stacked": "stacked",
  "flat side-by-side media-left": "side-by-side-media-left",
  "flat side-by-side media-right": "side-by-side-media-right",
};

export const ContentfulHeroTease = createContentfulComponent<
  HeroTeaseEntry,
  HeroTeaseOptions
>(({ entry, options, createEditAttributes }) => {
  const { getEntry, getEntryUrl } = useContentful();
  const headerAbove = options?.headerAbove || false;
  const heroTeaseProps = createHeroTeaseProps(entry.fields.tease);
  const mediaProps = createMediaAssetProps(entry.fields.media);
  const headerProps = entry.fields.header
    ? createComponentHeaderProps(entry.fields.header)
    : undefined;

  if (!heroTeaseProps) {
    return null;
  }

  if (entry.fields.overlineLink) {
    const overlineLink = getEntry(entry.fields.overlineLink);

    if (overlineLink) {
      heroTeaseProps.overline = {
        label: overlineLink.fields.text,
        link: getEntryUrl(overlineLink),
      };

      heroTeaseProps.editAttributes = {
        ...heroTeaseProps.editAttributes,
        overline: createEditAttributes({
          entry: overlineLink,
          fieldId: "text",
        }),
      };
    }
  }

  if (entry.fields.description) {
    heroTeaseProps.subheading = (
      <RenderContentfulRichText document={entry.fields.description} />
    );

    heroTeaseProps.editAttributes = {
      ...heroTeaseProps.editAttributes,
      subheading: createEditAttributes({ entry, fieldId: "description" }),
    };
  }

  if (!heroTeaseProps.media && mediaProps) {
    heroTeaseProps.media = mediaProps;
  }

  if (entry.fields.alignment) {
    heroTeaseProps.align = entry.fields.alignment;
  }

  if (entry.fields.hideByline) {
    heroTeaseProps.hideByline = entry.fields.hideByline;
  }

  if (headerProps) {
    heroTeaseProps.header = headerProps;
    heroTeaseProps.hasHeader = true;
  }

  heroTeaseProps.editAttributes = {
    ...heroTeaseProps.editAttributes,
    title: createEditAttributes({
      entry: entry.fields.tease,
      fieldId: "title",
    }),
  };

  return (
    <HeroTease
      {...heroTeaseProps}
      theme={entry.fields.theme}
      layout={entryLayoutToTeaseLayout[entry.fields.layout || "card"]}
      isTopper={false}
      headerAbove={headerAbove}
    />
  );
});
